import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Text, Float } from "@react-three/drei";
import { useNeural } from "@/store/neuralStore";
import { useRef } from "react";
import * as THREE from "three";

function AgentSphere({ pos, color, label, idx }: { pos: [number, number, number]; color: string; label: string; idx: number }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (ref.current) {
      ref.current.position.y = pos[1] + Math.sin(t + idx) * 0.3;
      ref.current.rotation.y = t * 0.4;
    }
  });
  return (
    <Float speed={1.5} rotationIntensity={0.4} floatIntensity={0.6}>
      <mesh ref={ref} position={pos}>
        <icosahedronGeometry args={[0.7, 1]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.6} wireframe={false} />
      </mesh>
      <Text position={[pos[0], pos[1] + 1.3, pos[2]]} fontSize={0.28} color="white" anchorX="center">
        {label}
      </Text>
    </Float>
  );
}

function ConnectionLines({ agents }: { agents: any[] }) {
  const positions = agents.map((_, i) => {
    const r = 4;
    const a = (i / agents.length) * Math.PI * 2;
    return new THREE.Vector3(Math.cos(a) * r, Math.sin(i * 0.7) * 1.5, Math.sin(a) * r);
  });
  return (
    <>
      {positions.map((p, i) => {
        const next = positions[(i + 1) % positions.length];
        const points = [p, next];
        const geom = new THREE.BufferGeometry().setFromPoints(points);
        return (
          <line key={i}>
            <primitive object={geom} attach="geometry" />
            <lineBasicMaterial color="#22d3ee" transparent opacity={0.4} />
          </line>
        );
      })}
    </>
  );
}

export function SpaceMode() {
  const agents = useNeural((s) => s.agents);

  return (
    <div className="relative h-full w-full">
      <div className="absolute top-3 left-3 z-10 glass rounded-lg px-3 py-1.5 text-xs font-mono">
        🚀 3D Space Explorer · drag to orbit · scroll to zoom
      </div>
      <Canvas camera={{ position: [0, 3, 9], fov: 60 }}>
        <ambientLight intensity={0.3} />
        <pointLight position={[10, 10, 10]} intensity={1.2} color="#22d3ee" />
        <pointLight position={[-10, -10, -10]} intensity={0.8} color="#a78bfa" />
        <Stars radius={100} depth={50} count={4000} factor={4} fade speed={1} />

        <ConnectionLines agents={agents} />

        {agents.map((a, i) => {
          const r = 4;
          const ang = (i / agents.length) * Math.PI * 2;
          return (
            <AgentSphere
              key={a.id}
              idx={i}
              pos={[Math.cos(ang) * r, Math.sin(i * 0.7) * 1.5, Math.sin(ang) * r]}
              color={a.color}
              label={a.name}
            />
          );
        })}

        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[1.2, 32, 32]} />
          <meshStandardMaterial color="#0ea5e9" emissive="#0ea5e9" emissiveIntensity={0.7} wireframe />
        </mesh>

        <OrbitControls enablePan autoRotate autoRotateSpeed={0.5} />
      </Canvas>
    </div>
  );
}
